import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { API } from 'utilities/api';
import { GlassButton } from 'dashboard/components/common';
import { libraryLabel } from 'utilities/libraryLabels';

/**
 * The controls for a section that shows library content.
 *
 * It replaces the component editor rather than sitting beside it, because
 * there is nothing on this section to edit: the words belong to the library,
 * and offering a text box here would invite someone to type a team member
 * into a page and wonder why it did not appear anywhere else.
 *
 * So the only choices are presentational — which content, how many, how
 * arranged — plus a direct way to go and edit the content itself, since
 * "where do I change this?" is the immediate next question.
 */


export default function LibraryBinding({ source, onPatch, brandId }) {
  const [pages, setPages] = useState([]);
  // The brand's own entries of this type, so a featured row is chosen from
  // what exists rather than described in the abstract.
  const [entries, setEntries] = useState([]);

  // Only detail pages for THIS content type can be a link target. Offering
  // every page would let someone point blog cards at the contact page, which
  // would render the contact layout around a blog post.
  useEffect(() => {
    if (!brandId) return;
    API.listSitePages({ brand_id: brandId })
      .then((res) => setPages((Array.isArray(res) ? res : [])
        .filter((pg) => pg.detailOf === source.libraryType)))
      .catch(() => setPages([]));
  }, [brandId, source.libraryType]);

  useEffect(() => {
    if (!brandId || !source.libraryType) { setEntries([]); return; }
    // Products are bound like a library type but are not one: they live in
    // the shop, with their own endpoint. Asking the content-library endpoint
    // for "products" returns nothing, so a shop full of stock would offer an
    // empty picker.
    const fetchEntries = source.libraryType === 'products'
      ? API.listShopProducts({ brand_id: brandId })
        .then((res) => (Array.isArray(res?.products) ? res.products : (Array.isArray(res) ? res : [])))
      : API.getStageByBrandId?.({ brand_id: brandId, stage: source.libraryType })
        .then((res) => (Array.isArray(res?.entries) ? res.entries : []));

    Promise.resolve(fetchEntries)
      .then((rows) => setEntries(rows || []))
      .catch(() => setEntries([]));
  }, [brandId, source.libraryType]);

  const navigate = useNavigate();
  const label = libraryLabel(source.libraryType);
  const picked = Array.isArray(source.entryIds) ? source.entryIds : [];
  const titleOf = (e) => e.title || e.name || e.question || 'Untitled';

  const set = (changes) => onPatch({ source: { ...source, ...changes } });

  return (
    <Box
      sx={{
        borderRadius: '10px',
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        p: '14px',
      }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between' gap='10px' mb='10px' flexWrap='wrap'>
        <Stack direction='row' alignItems='center' spacing='8px'>
          <LibraryBooksIcon fontSize='small' color='primary' />
          <Typography variant='body2' fontWeight='700'>{label}</Typography>
          <Chip size='small' variant='outlined' label='From library' />
        </Stack>
        <GlassButton
          size='small'
          endIcon={<OpenInNewIcon fontSize='small' />}
          onClick={() => navigate(`/dashboard/webpage/${source.libraryType}`)}
        >
          Edit content
        </GlassButton>
      </Stack>

      <Typography variant='caption' color='text.secondary' display='block' mb='12px'>
        This section shows your {label.toLowerCase()}. Changing the content updates every page
        that uses it — and switching this section off keeps all of it.
      </Typography>

      {/* Which entries.
          Nothing chosen means the most recent, which is right for a blog
          list that should keep itself current and wrong for a featured row —
          where the point is that somebody picked these three, and a new post
          must not push one of them out. */}
      <Box mb='10px'>
        <FormControl size='small' fullWidth>
          <InputLabel shrink>Which ones</InputLabel>
          <Select
            multiple
            displayEmpty
            value={picked}
            onChange={(e) => set({ entryIds: e.target.value })}
            input={<OutlinedInput notched label='Which ones' />}
            renderValue={(sel) => (sel.length === 0
              ? `The most recent ${source.limit || ''}`.trim()
              : `${sel.length} chosen`)}
          >
            {entries.length === 0 && (
              <MenuItem disabled value=''>
                Nothing in your {label.toLowerCase()} yet
              </MenuItem>
            )}
            {entries.map((e) => (
              <MenuItem key={e._id} value={e._id}>
                <Checkbox size='small' checked={picked.includes(e._id)} />
                <ListItemText primary={titleOf(e)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {picked.length > 0 && (
          <Stack direction='row' spacing='6px' flexWrap='wrap' useFlexGap mt='8px'>
            {picked.map((id) => {
              const e = entries.find((x) => x._id === id);
              return (
                <Chip
                  key={id}
                  size='small'
                  label={e ? titleOf(e) : 'Removed entry'}
                  color={e ? 'default' : 'warning'}
                  onDelete={() => set({ entryIds: picked.filter((x) => x !== id) })}
                />
              );
            })}
          </Stack>
        )}

        <Typography variant='caption' color='text.secondary' display='block' mt='6px'>
          {picked.length > 0
            ? 'Shown in the order above. Unpublishing one takes it off the page.'
            : 'Leave this empty to always show your newest, or pick specific ones to feature.'}
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing='10px'>
        <TextField
          size='small'
          type='number'
          label='How many'
          value={source.limit ?? 3}
          onChange={(e) => set({ limit: Number(e.target.value) })}
          inputProps={{ min: 0, max: 50 }}
          helperText={picked.length > 0 ? 'Not used — you picked them' : '0 shows all of them'}
          disabled={picked.length > 0}
          sx={{ width: { sm: 140 } }}
        />
        <FormControl size='small' sx={{ width: { sm: 160 } }}>
          <InputLabel>Arrangement</InputLabel>
          <Select
            label='Arrangement'
            value={source.display || 'grid'}
            onChange={(e) => set({ display: e.target.value })}
          >
            <MenuItem value='grid'>Grid</MenuItem>
            <MenuItem value='list'>List</MenuItem>
            <MenuItem value='carousel'>Carousel</MenuItem>
          </Select>
        </FormControl>
        {source.display === 'carousel' && (
          <>
            <TextField
              size='small'
              type='number'
              label='Show at a time'
              value={source.perView ?? 1}
              onChange={(e) => set({ perView: Number(e.target.value) })}
              inputProps={{ min: 1, max: 6 }}
              helperText='side by side'
              sx={{ width: { sm: 150 } }}
            />
            <TextField
              size='small'
              type='number'
              label='Rotate every'
              value={source.rotateSeconds ?? 4}
              onChange={(e) => set({ rotateSeconds: Number(e.target.value) })}
              inputProps={{ min: 2, max: 30 }}
              helperText='seconds'
              sx={{ width: { sm: 140 } }}
            />
          </>
        )}
      </Stack>

      <Box mt='12px'>
        <FormControl size='small' fullWidth>
          <InputLabel shrink>When someone clicks an item</InputLabel>
          <Select
            displayEmpty
            label='When someone clicks an item'
            value={source.linkPageSlug || ''}
            onChange={(e) => set({ linkPageSlug: e.target.value })}
          >
            <MenuItem value=''>Nothing happens</MenuItem>
            {pages.map((pg) => (
              <MenuItem key={pg._id} value={pg.slug}>Open “{pg.title}”</MenuItem>
            ))}
          </Select>
        </FormControl>
        {pages.length === 0 && (
          <Typography variant='caption' color='text.secondary' display='block' mt='6px'>
            To make these clickable, create a page and set it as the detail page for {label
              .toLowerCase()} in its settings. One page serves every entry.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
